<template>
  <NModal
    :bordered="false"
    class="cb-custom-data-table-settings"
    :onEsc="() => setComponentEditMode(false)"
    preset="card"
    :show="true"
    size="huge"
    :style="{
      '--n-border-radius': '16px',
      '--n-padding-top': '16px',
      '--n-padding-right': '16px',
      '--n-padding-bottom': '16px',
      '--n-padding-left': '16px',
    }"
    :title="t('canvasUI.canvasBuilder.customDataTable.editCustomDataTableComponent')"
    @close="setComponentEditMode(false)"
  >
    <NCard
      aria-modal="true"
      :bordered="false"
      class="flex"
      embedded
      role="dialog"
      :style="{
        '--n-color-embedded-modal': themeVars.base,
        '--n-padding-top': 0,
        '--n-padding-right': 0,
        '--n-padding-bottom': 0,
        '--n-padding-left': 0,
      }"
    >
      <div class="cb-custom-data-table-settings__card overflow-auto">
        <div class="flex flex-col h-full">
          <div class="flex flex-col h-full">
            <div class="flex-grow overflow-y-auto overflow-x-hidden">
              <NSteps v-model:current="currentStep" class="mx-1 mt-1 mb-8" size="small">
                <NStep :title="t('canvasUI.canvasBuilder.customDataTable.configureHierarchy')" />
                <NStep :disabled="!isHierarchyValid" :title="t('canvasUI.canvasBuilder.customDataTable.dataAndSort')" />
                <NStep
                  :disabled="!isHierarchyValid"
                  :title="t('canvasUI.canvasBuilder.customDataTable.headerFooter')"
                />
                <!-- Hidden: Filters step -->
                <!-- <NStep :disabled="!isHierarchyValid" :title="t('canvasUI.canvasBuilder.customDataTable.filters')" /> -->
                <NStep
                  :disabled="!isHierarchyValid"
                  :title="t('canvasUI.canvasBuilder.dynamicDataTable.customization')"
                />
              </NSteps>

              <!-- Step 1: Hierarchy -->
              <template v-if="currentStep === 1">
                <div class="flex flex-col pa-4 border-solid border-primary5 rounded-2" style="max-height: 530px">
                  <div class="font-500 tracking-[1.25px] mb-2">
                    {{ t('canvasUI.canvasBuilder.customDataTable.configureHierarchy') }}
                  </div>

                  <div ref="hierarchyScrollContainer" class="flex flex-col gap-4 mt-4 overflow-y-auto flex-1">
                    <div v-for="(level, idx) in hierarchyLevels" :key="`level-${idx}`" class="flex gap-3">
                      <div
                        class="flex-1 p-3 bg-primary6-1 rounded-2 border border-solid"
                        :style="{ borderColor: themeVars.primary5 }"
                      >
                        <div class="flex items-center justify-between mb-3">
                          <span class="font-medium"
                            >{{ t('canvasUI.canvasBuilder.customDataTable.level') }} {{ idx + 1 }}</span
                          >
                          <NRadioGroup v-model:value="level.type" size="small">
                            <NRadio value="group">
                              {{ t('canvasUI.canvasBuilder.customDataTable.group') }}
                            </NRadio>
                            <NRadio value="data">
                              {{ t('canvasUI.canvasBuilder.customDataTable.data') }}
                            </NRadio>
                          </NRadioGroup>
                        </div>
                        <div class="flex gap-2">
                          <div class="flex-1">
                            <div class="text-sm text-gray-500 mb-1">
                              {{ t('canvasUI.canvasBuilder.customDataTable.path') }}
                            </div>
                            <CInput
                              v-model:value="level.path"
                              :placeholder="t('canvasUI.canvasBuilder.customDataTable.pathPlaceholder')"
                            />
                          </div>
                          <div v-if="level.type === 'group'" class="flex-1">
                            <div class="text-sm text-gray-500 mb-1">
                              {{ t('canvasUI.canvasBuilder.customDataTable.groupByKey') }}
                            </div>
                            <CInput
                              v-model:value="level.group_by_key"
                              :placeholder="t('canvasUI.canvasBuilder.customDataTable.groupByKeyPlaceholder')"
                            />
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center">
                        <NButton circle secondary size="small" type="error" @click="removeHierarchyLevel(idx)">
                          <template #icon>
                            <CIcon icon="trash" size="16" />
                          </template>
                        </NButton>
                      </div>
                    </div>
                  </div>

                  <div class="mt-4">
                    <CButton bordered class="w-full" @click="addHierarchyLevel">
                      <template #icon>
                        <CIcon icon="plus" size="14" />
                      </template>
                      {{ t('canvasUI.canvasBuilder.customDataTable.addLevel') }}
                    </CButton>
                  </div>
                </div>
              </template>

              <!-- Step 2: Data & Sort -->
              <template v-if="currentStep === 2">
                <div class="flex flex-col" style="max-height: 530px">
                  <div v-if="selectedColumns.length > 0" ref="columnsScrollContainer" class="flex-1 overflow-y-auto">
                    <VueDraggable v-model="selectedColumns" class="flex flex-col gap-2" handle=".drag-handle">
                      <div v-for="col in selectedColumns" :key="col.key" class="flex items-center gap-2">
                        <CIcon class="drag-handle mr-2 cursor-move" icon="grip-dots-vertical" size="20" />
                        <div class="flex-grow rounded-md px-4 py-2 bg-base bg-primary6">
                          <div class="flex items-center justify-between gap-2">
                            <div class="font-500 flex-shrink-0 color-primary text-base">{{ col.key }}</div>
                            <div class="flex items-center gap-2 flex-1 justify-end">
                              <CInput
                                v-model:value="col.display_name"
                                class="bg-primary6 border-primary6 flex-1 max-w-[400px]"
                                :input-props="{ class: 'border-primary6' }"
                                :placeholder="t('canvasUI.canvasBuilder.dynamicDataTable.displayName')"
                                size="small"
                                :style="{ '--n-border': `1px solid ${themeVars.primary5}` }"
                              />
                              <NCheckbox v-model:checked="col.sort.enabled" size="small">
                                {{ t('canvasUI.canvasBuilder.dynamicDataTable.sortable') }}
                              </NCheckbox>
                            </div>
                          </div>
                          <div
                            v-if="col.sort.enabled"
                            class="flex gap-2 justify-between items-center mt-2 pt-2 border-t border-t-solid border-primary5"
                          >
                            <div
                              class="flex gap-2 items-center font-medium"
                              :class="{
                                'opacity-30': !col.sort.default,
                              }"
                            >
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.sorting') }}
                              <CButton
                                :disabled="!col.sort.default"
                                size="tiny"
                                :type="col.sort.direction === 'asc' ? 'primary' : 'secondary'"
                                @click="col.sort.direction = 'asc'"
                              >
                                {{ t('canvasUI.canvasBuilder.dynamicDataTable.asc') }}
                              </CButton>
                              <CButton
                                :disabled="!col.sort.default"
                                size="tiny"
                                :type="col.sort.direction === 'desc' ? 'primary' : 'secondary'"
                                @click="col.sort.direction = 'desc'"
                              >
                                {{ t('canvasUI.canvasBuilder.dynamicDataTable.desc') }}
                              </CButton>
                            </div>
                            <CButton
                              bordered
                              size="small"
                              :type="col.sort.default ? 'primary' : 'secondary'"
                              @click="setDefaultSort(col.key)"
                            >
                              <template #icon>
                                <CIcon v-if="col.sort.default" icon="check" size="12" />
                              </template>
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.default') }}
                            </CButton>
                          </div>
                          <div
                            class="flex gap-2 justify-between items-center mt-2 pt-2 border-t border-t-solid border-primary5"
                          >
                            <div class="flex gap-2 items-center font-medium">
                              {{ t('canvasUI.canvasBuilder.dynamicDataTable.format') }}
                            </div>
                            <div class="flex gap-2 items-center">
                              <CSelect
                                class="w-[120px]"
                                clearable
                                :options="formatTypeOptions"
                                :placeholder="t('canvasUI.common.none')"
                                size="small"
                                :value="col.format?.type"
                                @update:value="(value: string | null) => updateColumnFormat(col.key, 'type', value)"
                              />
                              <template v-if="col.format?.type === 'currency'">
                                <CSelect
                                  class="w-[80px]"
                                  :options="currencyOptions"
                                  placeholder="USD"
                                  size="small"
                                  :value="col.format?.currency ?? 'USD'"
                                  @update:value="
                                    (value: string | null) => updateColumnFormat(col.key, 'currency', value)
                                  "
                                />
                              </template>
                              <template
                                v-if="
                                  col.format?.type === 'number' ||
                                  col.format?.type === 'currency' ||
                                  col.format?.type === 'percentage'
                                "
                              >
                                <NInputNumber
                                  class="w-[80px]"
                                  :max="10"
                                  :min="0"
                                  placeholder="2"
                                  size="small"
                                  :value="col.format?.decimals ?? 2"
                                  @update:value="(val) => updateColumnFormat(col.key, 'decimals', val)"
                                />
                                <NCheckbox
                                  :checked="col.format?.thousandsSeparator ?? true"
                                  size="small"
                                  @update:checked="(val) => updateColumnFormat(col.key, 'thousandsSeparator', val)"
                                >
                                  {{ t('canvasUI.canvasBuilder.dynamicDataTable.thousandsSeparator') }}
                                </NCheckbox>
                              </template>
                              <template v-if="col.format?.type === 'date'">
                                <CSelect
                                  class="w-[140px]"
                                  :options="dateFormatOptions"
                                  placeholder="MM/DD/YYYY"
                                  size="small"
                                  :value="col.format?.dateFormat ?? 'MM/DD/YYYY'"
                                  @update:value="(val: string | null) => updateColumnFormat(col.key, 'dateFormat', val)"
                                />
                              </template>
                            </div>
                          </div>
                        </div>
                        <div class="flex items-center">
                          <NButton circle secondary size="small" type="error" @click="removeColumn(col.key)">
                            <template #icon>
                              <CIcon icon="trash" size="16" />
                            </template>
                          </NButton>
                        </div>
                      </div>
                    </VueDraggable>
                  </div>

                  <div class="mt-4">
                    <template v-if="!showAddColumnForm">
                      <CButton bordered class="w-full" @click="showAddColumnForm = true">
                        <template #icon>
                          <CIcon icon="plus" size="14" />
                        </template>
                        {{ t('canvasUI.canvasBuilder.customDataTable.addColumn') }}
                      </CButton>
                    </template>
                    <template v-else>
                      <div class="pa-4 border-solid border-primary5 rounded-2 relative">
                        <NButton circle class="absolute top-2 right-2" quaternary size="tiny" @click="cancelAddColumn">
                          <template #icon>
                            <CIcon icon="xmark" size="14" />
                          </template>
                        </NButton>
                        <div class="font-500 tracking-[1.25px] mb-2">
                          {{ t('canvasUI.canvasBuilder.customDataTable.addColumn') }}
                        </div>
                        <CInput
                          v-model:value="newColumnKey"
                          :placeholder="t('canvasUI.canvasBuilder.customDataTable.addColumnPlaceholder')"
                          size="small"
                          @keyup.enter="addColumn"
                        />
                        <CButton bordered class="w-full mt-2" @click="addColumn">
                          <template #icon>
                            <CIcon icon="plus" size="14" />
                          </template>
                          {{ t('canvasUI.canvasBuilder.customDataTable.addColumn') }}
                        </CButton>
                      </div>
                    </template>
                  </div>
                </div>
              </template>

              <!-- Step 3: Header/Footer -->
              <template v-if="currentStep === 3">
                <div class="flex flex-col pa-4 border-solid border-primary5 rounded-2" style="max-height: 530px">
                  <!-- Header Section -->
                  <div class="mb-6">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.header') }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.headerHelp') }}
                    </div>
                    <CInput
                      v-model:value="headerPath"
                      :placeholder="t('canvasUI.canvasBuilder.customDataTable.headerPathPlaceholder')"
                      size="small"
                    />
                  </div>

                  <!-- Footer Section -->
                  <div>
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.footer') }}
                    </div>
                    <div class="text-sm text-gray-500 mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.footerHelp') }}
                    </div>
                    <CInput
                      v-model:value="footerPath"
                      :placeholder="t('canvasUI.canvasBuilder.customDataTable.footerPathPlaceholder')"
                      size="small"
                    />
                  </div>
                </div>
              </template>

              <!-- Step 4: Filters (Hidden) -->
              <!-- <template v-if="currentStep === 4">
                <CQueryBuilder v-model="filters" :attributes="columnOptions" class="max-h-[530px] overflow-y-auto" />
              </template> -->

              <!-- Step 4: Customization -->
              <template v-if="currentStep === 4">
                <div class="flex-1 max-h-[530px] overflow-y-auto">
                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.fontFamily') }}
                    </div>
                    <CFontSelect v-model="tableTheme.font_family" bordered class="!w-1/2" render-font-preview />
                  </div>

                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.headerColor') }}
                    </div>
                    <NColorPicker
                      v-model:value="tableTheme.background_color"
                      :actions="['clear', 'confirm']"
                      class="!w-1/2"
                      :default-value="null"
                      :modes="['hex']"
                      :swatches="palette"
                      :updateInputModelOnDrop="true"
                    />
                  </div>

                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.groupHeaderColor') }}
                    </div>
                    <NColorPicker
                      v-model:value="tableTheme.group_header_color"
                      :actions="['clear', 'confirm']"
                      class="!w-1/2"
                      :default-value="null"
                      :modes="['hex']"
                      :swatches="palette"
                      :updateInputModelOnDrop="true"
                    />
                  </div>

                  <div class="mt-4">
                    <div class="font-500 tracking-[1.25px] mb-2">
                      {{ t('canvasUI.canvasBuilder.customDataTable.rowStriping') }}
                    </div>
                    <NCheckbox v-model:checked="enableRowStriping">
                      {{ t('canvasUI.canvasBuilder.customDataTable.enableRowStriping') }}
                    </NCheckbox>

                    <div v-if="enableRowStriping" class="mt-4 pl-6">
                      <div class="font-500 tracking-[1.25px] mb-2">
                        {{ t('canvasUI.canvasBuilder.customDataTable.stripingColor') }}
                      </div>
                      <NColorPicker
                        v-model:value="stripingColor"
                        :actions="['clear', 'confirm']"
                        class="!w-1/2"
                        :default-value="null"
                        :modes="['hex']"
                        :swatches="[themeVars.primary6_1, themeVars.primary6, ...palette]"
                        :updateInputModelOnDrop="true"
                      />

                      <div class="mt-4">
                        <div class="font-500 tracking-[1.25px] mb-2">
                          {{ t('canvasUI.canvasBuilder.customDataTable.stripingStartPosition') }}
                        </div>
                        <NRadioGroup v-model:value="stripingStartFromFirst" class="mt-2">
                          <div class="flex gap-6">
                            <NRadio :value="false">
                              {{ t('canvasUI.canvasBuilder.customDataTable.startFromSecondRow') }}
                            </NRadio>
                            <NRadio :value="true">
                              {{ t('canvasUI.canvasBuilder.customDataTable.startFromFirstRow') }}
                            </NRadio>
                          </div>
                        </NRadioGroup>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </NCard>
    <template #footer>
      <div class="flex justify-between gap-4">
        <CButton @click="onCancel">
          {{ t('canvasUI.common.cancel') }}
        </CButton>

        <div class="flex gap-2">
          <CButton class="w-10 h-10" :disabled="currentStep === 1" @click="currentStep--">
            <template #icon>
              <CIcon icon="arrow-left" size="16px" />
            </template>
          </CButton>
          <CButton class="w-10 h-10" :disabled="currentStep === 4 || !isHierarchyValid" @click="currentStep++">
            <template #icon>
              <CIcon icon="arrow-right" size="16px" />
            </template>
          </CButton>
          <CButton :disabled="!isHierarchyValid || !selectedColumns.length" type="primary" @click="onSave">
            {{ t('canvasUI.common.save') }}
          </CButton>
        </div>
      </div>
    </template>
  </NModal>
</template>

<script setup lang="ts">
import {
  NButton,
  NCard,
  NCheckbox,
  NColorPicker,
  NInputNumber,
  NModal,
  NRadio,
  NRadioGroup,
  NStep,
  NSteps,
} from 'naive-ui'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { VueDraggable } from 'vue-draggable-plus'

import CButton from '@lib/components/CButton/CButton.vue'
import CFontSelect from '@lib/components/CFontSelect/CFontSelect.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CInput from '@lib/components/CInput/CInput.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
// import CQueryBuilder from '@lib/components/CQueryBuilder/CQueryBuilder.vue'

import useCanvas from '@canvas-builder/composables/useCanvas'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import useThemeVars from '@lib/composables/useThemeVars'

import type {
  ComponentNode,
  CustomDataTableProps,
  CustomDataTableHierarchyLevel,
  DynamicDatatableProperty,
} from '@canvas-builder/types/canvas'
import type { QueryAttribute, QueryOperator } from '@lib/types/query-builder.types'

import { DEFAULT_FONT } from '@canvas-builder/constants'

const props = defineProps<{
  id: ComponentNode['id']
  data: CustomDataTableProps | null
}>()

const { t } = useI18n()
const { setComponentEditMode, updateNodeDataById } = useCanvas()
const { palette } = useCanvasTheme()
const themeVars = useThemeVars()

const snapshot = ref(JSON.parse(JSON.stringify(props.data)))
const currentStep = ref(1)
const hierarchyScrollContainer = ref<HTMLDivElement | null>(null)
const columnsScrollContainer = ref<HTMLDivElement | null>(null)
const hierarchyLevels = ref<CustomDataTableHierarchyLevel[]>(
  props.data?.hierarchy?.length
    ? JSON.parse(JSON.stringify(props.data.hierarchy))
    : [{ type: 'data' as const, path: '', group_by_key: '' }],
)
const selectedColumns = ref<DynamicDatatableProperty[]>(
  props.data?.columns?.length
    ? JSON.parse(JSON.stringify(props.data.columns)).map((col: DynamicDatatableProperty) => ({
        ...col,
        sort: col.sort ?? { enabled: false, direction: 'asc', default: false },
      }))
    : [],
)
const filters = ref<QueryAttribute[]>(
  props.data?.filters
    ? props.data.filters.map((f) => ({
        property: f.property,
        operator: f.operator as QueryOperator,
        value: f.value,
      }))
    : [],
)
const tableTheme = ref({
  background_color: props.data?.theme?.background_color ?? null,
  group_header_color: props.data?.theme?.group_header_color ?? null,
  font_family: props.data?.theme?.font_family ?? DEFAULT_FONT,
})
const enableRowStriping = ref(props.data?.enable_row_striping !== false)
const stripingColor = ref<string | undefined>(
  props.data?.enable_row_striping !== false ? (props.data?.striping_color ?? themeVars.value.primary6_1) : undefined,
)
const stripingStartFromFirst = ref(props.data?.striping_start_from_first ?? false)
const headerPath = ref(props.data?.header_path ?? '')
const footerPath = ref(props.data?.footer_path ?? '')
const newColumnKey = ref('')
const showAddColumnForm = ref(false)

const isHierarchyValid = computed(() => {
  return hierarchyLevels.value.length > 0 && hierarchyLevels.value.every((level) => level.path)
})

// const columnOptions = computed(() =>
//   selectedColumns.value.map((col) => ({
//     label: col.display_name || col.key,
//     value: col.key,
//   })),
// )

const formatTypeOptions = computed(() => [
  { label: t('canvasUI.common.none'), value: undefined },
  { label: t('canvasUI.canvasBuilder.customDataTable.currency'), value: 'currency' },
  { label: t('canvasUI.canvasBuilder.customDataTable.number'), value: 'number' },
  { label: t('canvasUI.canvasBuilder.customDataTable.percentage'), value: 'percentage' },
  { label: t('canvasUI.canvasBuilder.customDataTable.date'), value: 'date' },
])

const currencyOptions = computed(() => [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' },
  { label: 'JPY', value: 'JPY' },
  { label: 'CHF', value: 'CHF' },
  { label: 'CAD', value: 'CAD' },
  { label: 'AUD', value: 'AUD' },
])

const dateFormatOptions = computed(() => [
  { label: 'MM/DD/YYYY', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY', value: 'DD/MM/YYYY' },
  { label: 'YYYY-MM-DD', value: 'YYYY-MM-DD' },
  { label: 'MMM DD, YYYY', value: 'MMM DD, YYYY' },
  { label: 'DD MMM YYYY', value: 'DD MMM YYYY' },
])

function addHierarchyLevel() {
  hierarchyLevels.value.push({ type: 'data', path: '', group_by_key: '' })

  nextTick(() => {
    if (hierarchyScrollContainer.value) {
      hierarchyScrollContainer.value.scrollTop = hierarchyScrollContainer.value.scrollHeight
    }
  })
}

function removeHierarchyLevel(idx: number) {
  hierarchyLevels.value.splice(idx, 1)
}

function addColumn() {
  if (!newColumnKey.value.trim()) return

  selectedColumns.value.push({
    key: newColumnKey.value.trim(),
    display_name: '',
    sort: { enabled: false, direction: 'asc', default: false },
    format: {},
  })
  newColumnKey.value = ''

  nextTick(() => {
    if (columnsScrollContainer.value) {
      columnsScrollContainer.value.scrollTop = columnsScrollContainer.value.scrollHeight
    }
  })
}

function cancelAddColumn() {
  newColumnKey.value = ''
  showAddColumnForm.value = false
}

function removeColumn(key: string) {
  selectedColumns.value = selectedColumns.value.filter((col) => col.key !== key)
}

function updateColumnFormat(columnKey: string, formatKey: string, value: any) {
  selectedColumns.value = selectedColumns.value.map((col) => {
    if (col.key === columnKey) {
      if (!col.format) {
        col.format = {}
      }
      return {
        ...col,
        format: {
          ...col.format,
          [formatKey]: value,
        },
      }
    }
    return col
  })
}

function setDefaultSort(key: string) {
  selectedColumns.value = selectedColumns.value.map((col) => ({
    ...col,
    sort: {
      ...col.sort,
      direction: col.sort.direction || 'asc',
      default: col.key === key && !col.sort.default,
    },
  }))
}

function onCancel() {
  if (snapshot.value) {
    updateNodeDataById(props.id, snapshot.value)
  }
  setComponentEditMode(false)
}

function onSave() {
  const nodeData: CustomDataTableProps = {
    hierarchy: hierarchyLevels.value,
    columns: selectedColumns.value,
    filters: filters.value,
    header_path: headerPath.value || undefined,
    footer_path: footerPath.value || undefined,
    theme: tableTheme.value,
    enable_row_striping: enableRowStriping.value,
    striping_color: enableRowStriping.value ? stripingColor.value : undefined,
    striping_start_from_first: enableRowStriping.value ? stripingStartFromFirst.value : undefined,
  }

  updateNodeDataById(props.id, nodeData)
  setComponentEditMode(false)
}
</script>

<style lang="scss">
.cb-custom-data-table-settings {
  width: 780px;
  height: clamp(350px, calc(100vh - 120px), 720px);
}

.cb-custom-data-table-settings__card {
  max-height: clamp(100px, calc(100vh - 260px), 588px);
}
</style>
